'use client'

import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { ClubsBoard } from '@/features/clubs-leaderboard/components/ClubsBoard'
import {
  isLeaderboardSportTabValue,
  resolveLeaderboardSportApiValue
} from '@/features/leaderboard/buildLeaderboardSportTabs'
import { LeaderboardLoading } from '@/features/leaderboard/components/LeaderboardBoard'
import { LeaderboardEmptyState } from '@/features/leaderboard/components/LeaderboardEmptyState'
import { LeaderboardSearch } from '@/features/leaderboard/components/LeaderboardSearch'
import { SportFilter } from '@/features/leaderboard/components/SportFilter'
import {
  buildLeaderboardPath,
  readLeaderboardSportFromUrl
} from '@/features/leaderboard/leaderboardUrl'
import { useSportTypes } from '@/features/sport-types/hooks'
import { useClubMe } from '@/hooks/useClubMe'
import { useUserMe } from '@/hooks/useUserMe'
import { useRouter } from '@/i18n/routing'

import S from './GlobalClubsLeaderboard.module.scss'

const SEARCH_DEBOUNCE_MS = 300

const GlobalClubsLeaderboard = () => {
  const t = useTranslations('clubsLeaderboard')
  const tb = useTranslations('leaderboardBoard')
  const router = useRouter()
  const searchParams = useSearchParams()
  const { data: sportTypes = [], isLoading: isSportTypesLoading } =
    useSportTypes('clubs')
  const { data: clubMe } = useClubMe()
  const { data: userMe } = useUserMe()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DEBOUNCE_MS
    )
    return () => clearTimeout(timer)
  }, [searchInput])

  const sportFromUrl = readLeaderboardSportFromUrl(searchParams.get('sport'))
  const sport = isLeaderboardSportTabValue(sportFromUrl, sportTypes)
    ? sportFromUrl
    : 'all'

  const setSport = (next: string) =>
    router.replace(buildLeaderboardPath({ audience: 'clubs', sport: next }), {
      scroll: false
    })

  const hasActiveFilters = sport !== 'all' || Boolean(search)
  const resetFilters = () => {
    setSearchInput('')
    setSearch('')
    setSport('all')
  }

  return (
    <div className={S.root}>
      <div className={S.toolbar}>
        <LeaderboardSearch
          value={searchInput}
          onChange={setSearchInput}
          placeholder={tb('searchClubs')}
          clearLabel={tb('clearSearch')}
        />
        <SportFilter
          value={sport}
          onChange={setSport}
          sportTypes={sportTypes}
        />
      </div>

      {isSportTypesLoading && sportFromUrl !== 'all' ? (
        <LeaderboardLoading />
      ) : (
        <ClubsBoard
          myClubId={
            clubMe?.club?.id ?? userMe?.user?.favorite_club_id ?? undefined
          }
          sportType={resolveLeaderboardSportApiValue(sport)}
          search={search || undefined}
          emptyState={
            <LeaderboardEmptyState
              title={t('emptyState.title')}
              description={
                hasActiveFilters
                  ? t('emptyState.description')
                  : t('emptyState.descriptionNoFilters')
              }
              actionLabel={
                hasActiveFilters ? t('emptyState.clearFilters') : undefined
              }
              onAction={hasActiveFilters ? resetFilters : undefined}
            />
          }
        />
      )}
    </div>
  )
}

export default GlobalClubsLeaderboard
